<script>
  import { createEventDispatcher, onMount } from 'svelte';
  import { browser } from '$app/environment';
  import { captureEvent } from '#lib/utils/posthog.js';
  import { showCountryPopupStore } from '#lib/stores/countryStore.js';

  const dispatch = createEventDispatcher();

  export let show = false;
  export let detectedCountry = null;

  // Where we ship, from Shopify Markets via /api/markets (hub ledger
  // 2026-10-07-100). This popup used to keep its own table of 29 names, a copy
  // that can only drift from what checkout enforces. Names come from Shopify
  // too, so there is nothing here to keep in sync.
  let countries = [];
  let loading = true;
  let loadFailed = false;

  onMount(async () => {
    try {
      const res = await fetch('/api/markets');
      const body = await res.json();
      countries = body?.countries ?? [];
      loadFailed = Boolean(body?.unavailable) || countries.length === 0;
    } catch {
      loadFailed = true;
    } finally {
      loading = false;
    }
  });

  $: visitorIso = String(detectedCountry || '').toUpperCase();
  $: served = (code) => countries.some((c) => c.code === code);
  // Only once the list is in can we say "we don't ship there": nothing is
  // refused on a guess.
  $: visitorUnserved = countries.length > 0 && Boolean(visitorIso) && !served(visitorIso);
  // Shopify only names the countries it sells to, and this one is by
  // definition not among them, so the browser names it.
  $: visitorName = (() => {
    if (!visitorIso) return '';
    try {
      return new Intl.DisplayNames(['en'], { type: 'region' }).of(visitorIso) || visitorIso;
    } catch {
      return visitorIso;
    }
  })();

  // The visitor's own country when we ship there; otherwise nothing is chosen
  // and they pick one (a gift going somewhere we do ship).
  let selectedCountry = '';
  let touched = false;
  $: if (!touched && countries.length) {
    selectedCountry = served(visitorIso) ? visitorIso : '';
  }
  // The visitor we cannot ship to has to be told, once. The popup used to
  // stay shut for them: when IP and timezone agree, geolocation auto-accepts
  // the country and marks the popup seen (tryAutoAcceptCountry), so a shopper
  // in Norway saw a plug and a price and met the refusal only at Order now.
  // On the product page, once per session, and never over a country the
  // visitor chose themselves.
  $: if (browser && visitorUnserved && !show) maybeTellUnserved();
  function maybeTellUnserved() {
    if (window.location.pathname !== '/products/the-glorb/') return;
    if (localStorage.getItem('manuallySelectedCountry')) return;
    if (sessionStorage.getItem('unservedCountryNotice')) return;
    sessionStorage.setItem('unservedCountryNotice', 'true');
    showCountryPopupStore.set(true);
  }
  let reportedUnserved = false;
  $: if (visitorUnserved && show && !reportedUnserved) {
    reportedUnserved = true;
    captureEvent('country_unserved_shown', { country: visitorIso });
  }

  function handleSave() {
    if (!selectedCountry) return;
    dispatch('save', { country: selectedCountry });
    show = false;
  }

  // Closing keeps the choice only when there is one we can ship to. Before,
  // the corner close saved whatever was selected, including a country we
  // cannot sell to, which only "Order now" would then refuse.
  function handleClose() {
    if (selectedCountry && served(selectedCountry)) {
      dispatch('save', { country: selectedCountry });
    } else {
      dispatch('close');
    }
    show = false;
  }

  // "Want us in Norway?": the address and the country go to Klaviyo
  // (/api/klaviyo/market-request), so the refusal leaves a demand map.
  let email = '';
  let submitting = false;
  let submitted = false;
  let requestError = '';
  async function submitRequest() {
    if (submitting) return;
    requestError = '';
    submitting = true;
    try {
      const res = await fetch('/api/klaviyo/market-request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, country: visitorIso, productName: 'The GLORB' }),
      });
      const body = await res.json();
      if (!body?.success) throw new Error(body?.error || 'Request failed');
      submitted = true;
      captureEvent('market_request_submitted', { requested_country: visitorIso });
    } catch (err) {
      requestError = err.message || 'Something went wrong';
      captureEvent('market_request_failed', { requested_country: visitorIso });
    } finally {
      submitting = false;
    }
  }
</script>

{#if show}
  <div class="popup-overlay" role="dialog" aria-modal="true" tabindex="-1" on:keydown>
    <div class="popup-content ph-no-deadclick">
      <button aria-label="Close" class="close-button" data-testid="country-popup-close" on:click={handleClose}>
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-x"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
      </button>
      <div class="popup-header">
        <h2>Ship to</h2>
        <p>Select your country for shipping</p>
      </div>

      {#if visitorUnserved}
        <div class="unserved" data-testid="market-request-form">
          {#if submitted}
            <p class="unserved-title" data-testid="market-request-thanks">Thanks - we'll email you when GLORB ships to {visitorName}.</p>
          {:else}
            <p class="unserved-title">We don't ship to {visitorName} yet.</p>
            <p class="unserved-sub">Leave your email and we'll tell you when we do.</p>
            <form on:submit|preventDefault={submitRequest}>
              <input
                class="unserved-email"
                type="email"
                required
                placeholder="you@example.com"
                aria-label="Email address"
                autocomplete="email"
                bind:value={email}
                data-testid="market-request-email"
              />
              <button class="save-button" type="submit" disabled={submitting} data-testid="market-request-submit">
                {submitting ? 'One sec…' : 'Notify me'}
              </button>
            </form>
            {#if requestError}<p class="unserved-error" role="alert">{requestError}</p>{/if}
          {/if}
          <p class="unserved-sub gift">Sending it to someone elsewhere? Choose where:</p>
        </div>
      {/if}

      <div class="popup-body">
        {#if loading}
          <p class="note">Loading countries…</p>
        {:else if loadFailed}
          <p class="note" data-testid="country-list-unavailable">We couldn't load the countries we ship to just now. Please try again in a moment.</p>
        {:else}
          <select
            id="country-select"
            class="country-select"
            bind:value={selectedCountry}
            on:change={() => (touched = true)}
            data-testid="country-select"
          >
            <option value="" disabled>Choose a country</option>
            {#each countries as c (c.code)}
              <option value={c.code}>{c.name}</option>
            {/each}
          </select>
        {/if}
      </div>

      <div class="popup-footer">
        <button class="save-button" on:click={handleSave} disabled={!selectedCountry} data-testid="country-save">
          Save
        </button>
      </div>
    </div>
  </div>
{/if}

<style>
  .popup-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 9999;
    backdrop-filter: blur(4px);
  }
  
  
  .popup-content {
    background: white;
    border-radius: 16px;
    padding: 32px;
    max-width: 400px;
    width: 90%;
    max-height: 80vh;
    overflow-y: auto;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
    animation: popup-enter 0.3s ease-out;
    position: relative;
  }
  
  .close-button {
    position: absolute;
    top: 16px;
    right: 16px;
    cursor: pointer;
    z-index: 1;
    border: none;
    background-color: transparent;
  }
  
  @keyframes popup-enter {
    from {
      opacity: 0;
      transform: scale(0.9) translateY(20px);
    }
    to {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }
  
  .popup-header {
    text-align: center;
    margin-bottom: 24px;
  }
  
  .popup-header h2 {
    font-size: 24px;
    font-weight: 600;
    margin: 0 0 8px 0;
    color: #1a1a1a;
  }
  
  .popup-header p {
    font-size: 14px;
    color: #666;
    margin: 0;
  }
  

  
  .popup-body {
    margin-bottom: 24px;
    /* border-bottom: solid 1px black; */
  }

  select {
  padding: 0.5rem;
  font-size: 1rem;
  border-radius: 5px;
  border: solid 1px #9b9ea3;
  width: 100%;
  max-width: 100%;
  
  
  /* Safari-specific fixes */
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  
  /* Custom dropdown arrow */
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2'%3e%3cpolyline points='6,9 12,15 18,9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 0.5rem center;
  background-size: 16px;
  padding-right: 2.5rem;
  
  /* Remove default focus outline */
  &:focus {
    outline: none;
    border-color: #0ea5e9;
    box-shadow: 0 0 0 2px rgba(14, 165, 233, 0.2);
  }
  
  /* Safari-specific styling */
  &::-ms-expand {
    display: none;
  }
  
  /* Ensure consistent font rendering */
  font-family: inherit;
  line-height: 1.5;
}
option[value=""] {
    color: #888;
  }

  .country-select:focus {
    outline: none;
    border-color: #0ea5e9;
    box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.1);
  }
  
  .popup-footer {
    text-align: center;
  }
  
  .save-button {
    background: #1a1a1a;
    color: white;
    border: none;
    padding: 12px 32px;
    border-radius: 8px;
    font-size: 16px;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 0.2s ease;
    min-width: 120px;
    border: solid 1px transparent;
  }
  
  .save-button:hover {
    background: white;
    color: black;
    border: solid 1px black;
  }
  
  .save-button:active {
    transform: translateY(1px);
  }
  
  .save-button:disabled {
    opacity: 0.45;
    cursor: default;
  }

  .note {
    font-size: 14px;
    color: #666;
    margin: 0;
    text-align: center;
  }

  /* A visitor we cannot ship to: say so, and offer to tell them when we do. */
  .unserved {
    margin-bottom: 20px;
    text-align: center;
  }
  .unserved-title {
    font-size: 16px;
    color: #1a1a1a;
    margin: 0 0 4px;
  }
  .unserved-sub {
    font-size: 14px;
    color: #666;
    margin: 0 0 12px;
  }
  .unserved-sub.gift {
    margin: 20px 0 0;
  }
  .unserved form {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  .unserved-email {
    padding: 0.5rem;
    font-size: 16px;
    border-radius: 5px;
    border: solid 1px #9b9ea3;
    font-family: inherit;
  }
  .unserved-error {
    color: #c62828;
    font-size: 13px;
    margin: 8px 0 0;
  }

  /* Mobile adjustments */
  @media (max-width: 640px) {
    .popup-content {
      padding: 24px;
      margin: 16px;
      width: calc(100% - 32px);
    }
    
    .popup-header h2 {
      font-size: 20px;
    }
    
    .country-select {
      font-size: 16px; /* Prevents zoom on iOS */
    }
  }
</style>
